<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">Manage Leave Limits</h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      :list-options="list_options"
      @reset_table="reset_table"
      @options_changed="update_table($event)"
      @filter_changed="update_filter($event)"
      :schema="schema"
      :query-select-options="options"
      :new-to="{ name: 'leave_limit.create', params: { id: 0 } }"
      new-button-text="New Leave Limit"
    >
      <template slot="middle">
        <o-list-multi-actions
          :schema="schema"
          :selection="selected"
          @delete="doDeleteItems"
          @merge="doMergeItems"
          enable-delete
          enable-merge
        />
      </template>
    </o-list-toolbar>

    <b-table
      striped
      bordered
      :fields="fields"
      :items="items"
      head-variant="light"
      class="o-list"
      thead-class="o-gradient-header"
      @row-selected="onRowSelected"
      :selectable="selected.selectable"
      :key="selected.selectable"
      no-local-sorting
      @sort-changed="change_sort"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order === 'desc'"
    >
      <template v-slot:cell(_selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>

      <template #top-row="{ columns, fields }" v-if="filterRow">
        <filter-cell
          :key="field.key"
          v-for="field in fields"
          :field="field"
          :schema="schemaModel"
          :options="filter_row_options"
          :qbe="list_options.qbe"
          @filter="filter_changed"
        />
      </template>

      <template slot="cell(created_date)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'leave_limit.edit', params: { id: item.id } }"
          :when="first_column_key === 'created_date'"
        >
          <date-display
            :value="item.created_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template slot="cell(depot)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'leave_limit.edit', params: { id: item.id } }"
          :when="first_column_key === 'depot'"
        >
          <lookup-display
            :list="optionCodeDepotItemsById"
            :value="item.depot"
            display-field="description"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template #cell(description)="{ index, item }">
        <o-link-cell
          :to="{ name: 'leave_limit.edit', params: { id: item.id } }"
          :when="first_column_key === 'description'"
        >
          <default-display
            :value="item.description"
            default-value="-"
          ></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(limit)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'leave_limit.edit', params: { id: item.id } }"
          :when="first_column_key === 'limit'"
        >
          <number-display :value="item.limit" :decimals="0" />
        </o-link-cell>
      </template>
      <template slot="cell(modified_date)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'leave_limit.edit', params: { id: item.id } }"
          :when="first_column_key === 'modified_date'"
        >
          <date-display
            :value="item.modified_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>

      <template #cell(active_start_date)="{ index, item }">
        <o-link-cell
          :to="{ name: 'leave_limit.edit', params: { id: item.id } }"
          :when="first_column_key === 'active_start_date'"
        >
          <date-display
            :value="item.active_start_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>

      <template #cell(active_end_date)="{ index, item }">
        <o-link-cell
          :to="{ name: 'leave_limit.edit', params: { id: item.id } }"
          :when="first_column_key === 'active_end_date'"
        >
          <date-display
            :value="item.active_end_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'leave_limit.create' }"
          >create a new item</router-link
        >
      </template>
    </b-table>

    <o-list-paging
      :limit="list_options.limit"
      :page-options="rows_per_page_items"
      :offset="list_options.offset"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import {
  CreateCodeTableFetchMixin,
  CreateGroupPermissionMixin,
  CreateListPageMixin,
  MultiActionsMixin,
} from "../../../vue-ocom/mixins";
import {
  fieldToTableColumn,
  GetSearchFields,
} from "../../../vue-ocom/schema/schema_utils";
import { cleanUpModel } from "../../../vue-ocom/schema/schema_vuetify";
import schema from "../../schema/index";

import OListToolbar from "../../../vue-ocom/components/list/OListToolbar";
import OListPaging from "../../../vue-ocom/components/list/OListPaging";
import DefaultDisplay from "../../../vue-ocom/components/display/DefaultDisplay";
import DateDisplay from "../../../vue-ocom/components/display/DateDisplay";
import NumberDisplay from "../../../vue-ocom/components/display/NumberDisplay";
import BooleanDisplay from "../../../vue-ocom/components/display/BooleanDisplay";
import LookupDisplay from "../../../vue-ocom/components/display/LookupDisplay";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad";
import EmailIcon from "vue-material-design-icons/Email";
import LinkIcon from "vue-material-design-icons/Link";
import FilterCell from "../../../vue-ocom/components/list/filter/FilterCell.vue";

export default {
  components: {
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
    FilterCell,
  },
  mixins: [
    CreateListPageMixin("LeaveLimit", "leave_limit"),
    MultiActionsMixin,
    CreateGroupPermissionMixin("LeaveLimit"),
    CreateCodeTableFetchMixin(["CodeDepot", "CodeEmployeeType"]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(
        schema.LeaveLimit,
        {}
      ) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.LeaveLimit),

      filterRow: true /* Set to False to disable filter row */,
      filter_list: [
        { name: "Active", code: "active", options: { offset: 0, sort: "id" } },
        {
          name: "Non Active",
          code: "inactive",
          options: { offset: 0, sort: "id" },
        },
        { name: "All", code: "all", options: { offset: 0, sort: "id" } },
      ],

      user_column_selection:
        null /* If user can customise the columns set them here */,
      column_override_params: {
        /* Put any column overrides here key by fieldname */
      },
    };
  },
  computed: {
    options() {
      return {
        CodeDepot: {
          options: this.optionCodeDepotItems,
          textField: "description",
          valueField: "id",
        },
        CodeEmployeeType: {
          options: this.optionCodeEmployeeTypeItems,
          textField: "description",
          valueField: "id",
        },
      };
    },
    fields() {
      const vm = this;
      const selectedColumns = this.user_column_selection || [
        "depot",
        "description",
        "limit",
        "active_start_date",
        "active_end_date",
      ];
      const fields = selectedColumns.map(function (item) {
        return fieldToTableColumn(
          vm.schema,
          item,
          vm.column_override_params[item]
        );
      });

      return this.addSelectionColums(
        this.filterTableColumns(this.schema, fields)
      );
    },
    first_column_key() {
      if (this.fields) {
        return this.fields[0].key;
      } else {
        return null;
      }
    },
    filter_row_options() {
      // Options for the FilterRow components
      return {
        id: false /*No filter for this field */,
        created_date: {},
        modified_date: {},
        depot: {
          options: this.optionCodeDepotItems,
          loading: this.optionCodeDepotWorking,
          "text-field": "description",
          "value-field": "id",
          "sort-field": "description",
          "blank-option-text": "",
        },
        limit: {},
        active_start_date: {},
        active_end_date: {},
        description: {},
      };
    },
  },
};
</script>
